import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import SynchronizedAreaChart2 from '../../Elements/Chart2';
import { CoinContext } from '../../../context/CoinContext';
import ToggleSwitch from '../../Elements/ToggleSwitch';
import LoadingHomeSkeleton from '../../Elements/HomeLoader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { DotLoader } from '../../Elements/LogoLoader';
import Workers from '../worker/Workers';
import RevenuePayouts from '../revenue/Payouts';
import RevenueReward, { mergeRewardsByDate } from '../revenue/Reward';
import { useAccount } from '../../../context/AccountContext';

export interface HomeData {
    hashRate24: string;
    revenueYestersay: string;
    revenueTodayEst: string;
    minersOnline: string;
    minersOffline: string;
    networkData: {
        netHashRate: string;
        dailyRevenuePerTh: string;
        dailyRevenuePerThInDollar: string;
        difficulty: string;
        coinPrice: string;
    };
}

interface DataItem {
    time: string;
    hashrate: number;
}

interface ApiResponseItem {
    timeStamp: number;
    hash: number;
}

interface Payout {
    rewardDate: string;
    coin: string;
    avgHash: number;
    reward: number;
    transactionStatus: string;
    amount: number;
}

interface BalanceData {
    totalBalance: number;
    coin: string;
    rewards: any[];
    payouts: Payout[];
}
interface NavigateToWalletProps {
    NavigateToWallet: () => void;
}

const HomeData: React.FC<NavigateToWalletProps> = ({ NavigateToWallet }) => {
    const [dataPeriod, setDataPeriod] = useState("1");
    const [chartData, setChartData] = useState<DataItem[]>([]);
    const [listingData, setListingData] = useState<HomeData | null>(null);
    const [balance, setBalance] = useState<any>("-");
    const [loading, setLoading] = useState(false)
    const [hashrate, setHashrate] = useState<number | null>()
    const [totalPayout, setTotalPayout] = useState<number | null>()
    const [userWalletCheck, setUserWalletCheck] = useState<boolean | null>(null)
    const [toggleSwitchActive, setToggleSwitchActive] = useState(false)
    const [activeSubSection, setActiveSubSection] = useState<'WORKERS' | 'REVENUE' | 'PAYOUTS'>('WORKERS');
    const [payoutsData, setPayoutsData] = useState<any[]>([]);
    const [rewardsData, setRewardsData] = useState<any[]>([]);

    const coinContext = useContext(CoinContext);
    const { activeAccount } = useAccount();

    const fetchHomeData = (coin: string) => {
        setLoading(true)
        axios.get(`/pool/dashboard/get-dat-redis?coin=${coin}`)
            .then(response => {
                setListingData(response.data);
                setLoading(false)
            })
            .catch(error => {
                console.error('Error fetching home data:', error);
                setLoading(false)
            });
    };

    const fetchBalance = (coin: string) => {
        axios.get(`/pool/reward?coin=${coin}`)
            .then((response: { data: BalanceData }) => {
                setBalance(response?.data?.totalBalance);
                const totalPayoutSum = response.data.payouts.reduce((acc: number, payout: Payout) => {
                    if (payout.transactionStatus === "TRANSFER_COMPLETED") {
                        return acc + payout.amount;
                    }
                    return acc;
                }, 0);

                setTotalPayout(totalPayoutSum)
                setPayoutsData(response.data.payouts || []);
                setRewardsData(mergeRewardsByDate(response.data.rewards || []));
            })
            .catch((error: any) => {
                console.error('Error fetching balance:', error);
            });
    };

    const walletCheck = (coin: string) => {
        setUserWalletCheck(null)
        axios.get(`/pool/wallet?coin=${coin}`)
            .then((response: { data: any }) => {
                setUserWalletCheck(response.data.length === 0 ? true : false)
            })
            .catch((error: any) => {
                console.error('Error fetching account details:', error);
            });
    }

    const fetchHomeChartData = (dataPeriod: string, coin: string, steps: string) => {
        setToggleSwitchActive(true)
        axios.get(`/pool/date-hash-coord-redis?hours=${dataPeriod}&steps=${steps}&coin=${coin}`)
            .then(response => {
                const formattedData: DataItem[] = response.data.map((n: ApiResponseItem) => ({
                    time: formatTimeLabel(n.timeStamp),
                    hashrate: n.hash.toFixed(1),
                }));
                const totalHashrate = response.data.reduce((sum: number, item: ApiResponseItem) => sum + item.hash, 0);
                const dataLength = response.data.length;
                const calculatedHashrate = totalHashrate / dataLength;

                setHashrate(calculatedHashrate);
                setChartData(formattedData);
                setToggleSwitchActive(false)
            })
            .catch(error => {
                console.error('Error fetching chart data:', error);
                setChartData([]);
                setToggleSwitchActive(false)
            });
    };

    const formatTimeLabel = (timestamp: number): string => {
        return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const roundUpToEightDecimals = (num: any): string => {
        return typeof num == 'string' ? num : num.toFixed(8);
    };

    useEffect(() => {
        let isMounted = true;

        const fetchData = async () => {
            if (!coinContext || !isMounted) return;

            const coin = coinContext.selectedCoin;

            try {
                await Promise.all([
                    fetchHomeData(coin),
                    fetchBalance(coin),
                    fetchHomeChartData(dataPeriod, coin, dataPeriod === "1" ? "1m" : "15m"),
                    walletCheck(coin)
                ]);
            } catch (error) {
                    console.error("Error fetching data:", error);
                }
        };

        fetchData();

        return () => {
            isMounted = false;
        };
    }, [dataPeriod, coinContext?.selectedCoin]);

    // Refresh all data when active account changes
    useEffect(() => {
        if (activeAccount && coinContext) {
            const coin = coinContext.selectedCoin;
            Promise.all([
                fetchHomeData(coin),
                fetchBalance(coin),
                fetchHomeChartData(dataPeriod, coin, dataPeriod === "1" ? "1m" : "15m"),
                walletCheck(coin)
            ]).then(() => {
            }).catch((error) => {
                console.error("Error fetching data after account switch:", error);
            });
        }
    }, [activeAccount]);


    return (
        <div className="md:py-8 pb-6">
            <div className="flex flex-col lg:flex-row gap-6 p-4">
                {/* Left Side: Stats Cards */}
                <div className="lg:w-1/4">
                    <div className="grid grid-cols-2 lg:flex lg:flex-col gap-3 lg:gap-4">
                        <DataCard
                            loading={loading}
                            title={`Total Payout (${coinContext?.selectedCoin})`}
                            value={roundUpToEightDecimals(totalPayout ?? "-")}
                            icon="💰"
                        />
                        <DataCard
                            loading={loading}
                            title={`Yesterday Revenue (${coinContext?.selectedCoin})`}
                            value={roundUpToEightDecimals(listingData?.revenueYestersay ?? "-")}
                            icon="📈"
                        />
                        <DataCard
                            loading={listingData === null}
                            title={`Today's Est. Revenue (${coinContext?.selectedCoin})`}
                            value={roundUpToEightDecimals(listingData?.revenueTodayEst ?? "-")}
                            icon="📊"
                        />
                        <DataCard
                            loading={loading}
                            title={`Balance (${coinContext?.selectedCoin})`}
                            value={roundUpToEightDecimals(balance)}
                            icon="💳"
                        />
                    </div>
                </div>

                {/* Right Side: Graph */}
                <div className="lg:w-3/4 flex flex-col">
                    <div className='relative md:p-6 md:p-10 md:rounded-[2.5rem] md:border md:border-white/40 md:dark:border-slate-700/50 transition-all h-full flex flex-col'>
                        {toggleSwitchActive &&
                            <div className='absolute inset-0 flex items-center justify-center bg-white/50 dark:bg-slate-800/50 backdrop-blur-sm z-10 rounded-2xl'>
                                <DotLoader />
                            </div>
                        }
                        <div className='flex flex-col md:flex-row justify-between items-center mb-6'>
                            <div className='text-center md:text-left mb-6 md:mb-0'>
                                <p className='text-2xl md:text-4xl font-black text-[#0E4D92] dark:text-blue-400 mb-2 transition-colors tracking-tighter'>{`Hashrate`}</p>
                                <p className='text-xs md:text-sm font-bold text-slate-400 dark:text-slate-500 uppercase tracking-[0.2em] opacity-80'>Real-time mining power</p>
                            </div>
                            <div className="flex flex-wrap justify-center gap-2">
                                <div className="flex bg-slate-100/50 dark:bg-slate-900/50 backdrop-blur-sm p-1.5 rounded-2xl shadow-inner border border-white/10 dark:border-slate-700/30">
                                    <ButtonToggle
                                        label="1 hour"
                                        isActive={dataPeriod === '1'}
                                        onClick={() => setDataPeriod("1")}
                                        className="rounded-full"
                                        toggleSwitchActive={toggleSwitchActive}
                                    />
                                    <ButtonToggle
                                        label="24 hours"
                                        isActive={dataPeriod === '24'}
                                        onClick={() => setDataPeriod("24")}
                                        className="rounded-full"
                                        toggleSwitchActive={toggleSwitchActive}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="flex-grow bg-white/30 dark:bg-slate-900/40 rounded-3xl p-4 border border-white/20 dark:border-slate-800/50 shadow-inner">
                            <SynchronizedAreaChart2 data={chartData} />
                        </div>
                    </div>

                    <div className="mt-6 flex justify-center items-center gap-4">
                        <span className="inline-flex items-center gap-1.5 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 px-4 py-2 rounded-full border border-green-200 dark:border-green-500/30 shadow-sm">
                            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                            <span className="font-medium text-sm">Online:</span>
                            <span className="font-bold text-lg">{listingData?.minersOnline || 0}</span>
                        </span>
                        <span className="inline-flex items-center gap-1.5 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 px-4 py-2 rounded-full border border-red-200 dark:border-red-500/30 shadow-sm">
                            <div className="w-2 h-2 rounded-full bg-red-500"></div>
                            <span className="font-medium text-sm">Offline:</span>
                            <span className="font-bold text-lg">{listingData?.minersOffline || 0}</span>
                        </span>
                    </div>
                </div>
            </div>
            {userWalletCheck &&
                <div className="mx-5 my-6 overflow-hidden rounded-xl shadow-lg border border-red-100 dark:border-red-900/30">
                    <button onClick={NavigateToWallet} className="bg-white dark:bg-red-900/10 hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500 dark:text-red-400 p-4 w-full flex items-center justify-center gap-3 font-bold transition-all">
                        <FontAwesomeIcon icon={faExclamationTriangle} className="animate-pulse" />
                        <span className="tracking-wide uppercase text-sm md:text-base">{coinContext?.selectedCoin} WALLET MISSING</span>
                    </button>
                </div>
            }
            <div className="mt-8 px-4">
                {/* Premium Sub-section Switcher */}
                <div className="flex flex-wrap justify-center md:justify-start gap-2 md:gap-3 mb-6
                bg-slate-50 dark:bg-slate-900/50
                p-1.5 md:p-2 rounded-2xl
                w-full md:w-fit
                border border-gray-100 dark:border-slate-800">
                    {[
                        { id: 'WORKERS', label: 'Workers', count: listingData?.minersOnline || 0 },
                        { id: 'REVENUE', label: 'Revenue', count: rewardsData.length },
                        { id: 'PAYOUTS', label: 'Payouts', count: payoutsData.length }
                    ].map((section) => (
                        <button
                            key={section.id}
                            onClick={() => setActiveSubSection(section.id as any)}
                            className={`flex items-center justify-center gap-1.5 md:gap-2
                                        px-3 md:px-6 py-2 md:py-2.5
                                        rounded-xl text-xs md:text-sm font-bold
                                        transition-all duration-300
                                        flex-1 md:flex-none min-w-0 overflow-hidden
                                        ${activeSubSection === section.id
                                    ? 'bg-[#0A3A6B] text-white shadow-md md:shadow-lg shadow-blue-200/50 dark:shadow-none'
                                    : 'text-slate-600 dark:text-slate-400 hover:bg-white dark:hover:bg-slate-800 hover:text-[#0A3A6B] dark:hover:text-blue-300'
                                }`}
                        >
                            <span className="whitespace-nowrap truncate text-left flex-1">
                                {section.label}
                            </span>

                            <span className={`px-1.5 md:px-2 py-0.5 rounded-full text-[10px] md:text-xs font-bold flex-shrink-0
                                ${activeSubSection === section.id
                                    ? 'bg-white/20 text-white'
                                    : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300'
                                }`}>
                                {section.count}
                            </span>
                        </button>
                    ))}
                </div>


                <div className="bg-white dark:bg-slate-800/40 rounded-[2rem] border border-gray-100 dark:border-slate-800/50 shadow-sm overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-500">
                        {activeSubSection === 'WORKERS' && <Workers key="workers"/>}
                    {activeSubSection === 'REVENUE' && (
                        <div className="p-4 md:p-6">
                            <RevenueReward key="revenue" rewards={rewardsData} coin={coinContext?.selectedCoin || ""} />
                        </div>
                    )}
                    {activeSubSection === 'PAYOUTS' && (
                        <div className="p-4 md:p-6">
                            <RevenuePayouts key="payouts" payouts={payoutsData} coin={coinContext?.selectedCoin || ""} />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const DataCard: React.FC<{
    title: string;
    value: any;
    loading: boolean;
    icon?: string;
}> = ({ title, value, loading, icon }) => {
    // Unified dark blue color scheme with different shades
    const colorScheme = {
        primary: 'text-[#0A3A6B] dark:text-blue-400',
        bgLight: 'bg-blue-50',
        bgDark: 'bg-blue-900/20',
        borderLight: 'border-blue-100',
        borderDark: 'border-blue-800/50',
        accentLight: 'bg-blue-200/30',
        accentDark: 'bg-blue-700/20',
    };

    return (
        <div className={`${colorScheme.bgLight} dark:${colorScheme.bgDark} backdrop-blur-sm p-4 md:p-6 lg:p-7 rounded-2xl md:rounded-[1.75rem] lg:rounded-[2rem] shadow-sm border ${colorScheme.borderLight} dark:${colorScheme.borderDark} group hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 flex-1 relative overflow-hidden`}>
            {/* Background decorative element - different shades for depth */}
            <div className="absolute top-0 right-0 w-20 h-20 lg:w-24 lg:h-24 bg-gradient-to-br from-blue-100/30 to-blue-200/10 dark:from-blue-800/10 dark:to-blue-900/5 rounded-full -mr-8 lg:-mr-12 -mt-8 lg:-mt-12 blur-xl group-hover:scale-110 transition-transform duration-300" />

            {/* Additional subtle background element */}
            <div className="absolute bottom-0 left-0 w-12 h-12 bg-blue-50/20 dark:bg-blue-800/10 rounded-full -ml-4 -mb-4 blur-lg" />

            <div className="relative z-10">
                <div className="flex items-center justify-between mb-3 lg:mb-4">
                    <p className="text-[10px] md:text-[11px] lg:text-xs font-bold text-blue-600/80 dark:text-blue-400/80 uppercase tracking-[0.15em] flex-1 break-words overflow-visible">
                        {title}
                    </p>
                    {icon && (
                        <span className="text-lg lg:text-xl ml-2 text-blue-500 dark:text-blue-400 opacity-80 group-hover:scale-110 transition-transform duration-200 flex-shrink-0">{icon}</span>
                    )}
                </div>

                <div className="flex items-baseline gap-1 min-w-0">
                    <h3 className={`text-xl md:text-2xl lg:text-3xl xl:text-4xl font-black ${colorScheme.primary} transition-colors tracking-tight break-words overflow-visible w-full`}>
                        {loading ? (
                            <div className="flex items-center space-x-2">
                                <div className="h-6 lg:h-8 w-20 lg:w-32 bg-blue-100 dark:bg-blue-800/40 rounded-lg animate-pulse"></div>
                            </div>
                        ) : (
                            value
                        )}
                    </h3>
                </div>

                {/* Subtle bottom accent with gradient */}
                <div className="h-0.5 w-8 bg-gradient-to-r from-blue-400/50 to-blue-300/30 dark:from-blue-500/40 dark:to-blue-400/20 rounded-full mt-4 lg:mt-5 group-hover:w-12 transition-all duration-300" />
            </div>

            {/* Loading overlay */}
            {loading && !value && (
                <div className="absolute inset-0 bg-white/60 dark:bg-slate-900/60 backdrop-blur-[2px] rounded-2xl flex items-center justify-center">
                    <div className="flex flex-col items-center">
                        <div className="w-6 h-6 lg:w-8 lg:h-8 border-2 border-blue-300 dark:border-blue-600 border-t-blue-500 dark:border-t-blue-400 rounded-full animate-spin mb-2"></div>
                        <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">Loading...</span>
                    </div>
                </div>
            )}
        </div>
    );
};

const ButtonToggle: React.FC<{ label: string; isActive: boolean; onClick: () => void; className: string; toggleSwitchActive: boolean }> = ({ label, isActive, onClick, className, toggleSwitchActive }) => (
    <button
        onClick={() => !toggleSwitchActive && onClick()}
        className={`px-6 py-2 rounded-xl text-xs font-bold transition-all border ${isActive
            ? 'bg-[#0E4D92] text-white border-[#0E4D92] shadow-md shadow-blue-200 dark:shadow-none scale-105'
            : 'bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400 border-gray-100 dark:border-slate-700 hover:border-blue-200 dark:hover:border-slate-600'
            }`}
        disabled={isActive}
    >
        {label}
    </button>
);

export default HomeData;
